import type {
  ApprovedWorkflow,
  CustomerTaxonomy,
  GroundingEntryInput,
  GroundingModality,
  GroundingPolarity,
  GroundingProposal,
  GroundingSection
} from '../../api/workflows';

export type Concept = ApprovedWorkflow['nodes'][number];
export type TaxonomyNode = CustomerTaxonomy['nodes'][number];
export type GroundingEntry = ApprovedWorkflow['grounding'][number];
export type Section = GroundingSection;
export type Polarity = GroundingPolarity;

export const CSV_HEADER =
  'customer,workflow_key,entry_id,concept_id,taxonomy_path,section,polarity,modalities,content,source';

export const SECTION_ORDER: readonly Section[] = ['definition', 'keyword', 'example'];

export const SECTION_COPY: Record<Section, {title: string; label: string; description: string}> = {
  definition: {
    title: 'Definitions',
    label: 'Definition',
    description: 'How the classifier should understand this concept'
  },
  keyword: {title: 'Keywords', label: 'Keyword', description: 'Terms that signal this concept'},
  example: {
    title: 'Examples',
    label: 'Example',
    description: 'Content that belongs here or looks similar'
  }
};

export const POLARITY_COPY: Record<Polarity, string> = {
  positive: 'Positive',
  negative: 'Negative'
};

export const MODALITY_ORDER: readonly GroundingModality[] = ['text', 'image', 'video'];

export const MODALITY_COPY: Record<GroundingModality, string> = {
  text: 'Text',
  image: 'Image',
  video: 'Video'
};

/** An empty modality list means the entry applies to every modality. */
export function modalitiesLabel(modalities: readonly GroundingModality[]) {
  return modalities.length === 0 ? 'All' : modalities.map((m) => MODALITY_COPY[m]).join(', ');
}

/** One-line description of an entry's section, polarity and modalities, for diffs and CSV review. */
export function entryLabel(entry: {
  section: Section;
  polarity: Polarity;
  modalities: readonly GroundingModality[];
}) {
  return `${SECTION_COPY[entry.section].label} · ${POLARITY_COPY[entry.polarity]} · ${modalitiesLabel(entry.modalities)}`;
}

export type PendingConfig = {
  id: string;
  status: string;
  author: string;
  base_revision: string;
  configuration: {
    base_revision: string | null;
    pack_version: string;
    root_concept_ids: string[];
    name: string;
    routing_definition: string;
    routing_keywords: string;
    enabled: boolean;
    include_non_violative?: boolean;
  };
};

export type PendingArchive = {id: string; author: string};

export type PendingGround = GroundingProposal;

/** Values the configure dialog edits; becomes a WorkflowConfigurationRequest on submit. */
export type ConfigDraft = {
  name: string;
  roots: string[];
  routing_definition: string;
  routing_keywords: string;
  enabled: boolean;
  include_non_violative: boolean;
};

/** Grounding edits held in the browser until the contributor submits them as one proposal. */
export type StagedAddition = {
  tmp: string;
  concept_id: string;
  section: Section;
  polarity: Polarity;
  modalities: GroundingModality[];
  content: string;
};

export type Stage = {
  edits: Record<string, GroundingEntryInput>;
  retired: Record<string, true>;
  added: StagedAddition[];
};

export type SetStage = (update: (stage: Stage) => Stage) => void;

export const emptyStage = (): Stage => ({edits: {}, retired: {}, added: []});

export function stageCount(stage: Stage) {
  const retired = Object.keys(stage.retired).length;
  const changed = Object.keys(stage.edits).filter((id) => !stage.retired[id]).length;
  return {
    changed,
    retired,
    added: stage.added.length,
    total: changed + retired + stage.added.length
  };
}

/** The full grounding set the API expects: every published entry that survives, plus additions. */
export function stagedEntries(grounding: GroundingEntry[], stage: Stage): GroundingEntryInput[] {
  return [
    ...grounding
      .filter((entry) => !stage.retired[entry.id])
      .map((entry) => ({
        entry_id: entry.id,
        concept_id: entry.concept_id,
        section: stage.edits[entry.id]?.section ?? entry.section,
        polarity: stage.edits[entry.id]?.polarity ?? entry.polarity,
        modalities: stage.edits[entry.id]?.modalities ?? entry.modalities,
        content: stage.edits[entry.id]?.content ?? entry.content
      })),
    ...stage.added.map((added) => ({
      entry_id: null,
      concept_id: added.concept_id,
      section: added.section,
      polarity: added.polarity,
      modalities: added.modalities,
      content: added.content
    }))
  ];
}

export function short(value: string | null | undefined) {
  return value ? value.slice(0, 8) : '—';
}

export function label(node: TaxonomyNode) {
  return node.display_name || node.name;
}

export function fuzzyMatch(value: string, query: string) {
  const text = value.toLocaleLowerCase();
  const terms = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
  return terms.every((term) => {
    if (text.includes(term)) {
      return true;
    }

    let at = 0;
    for (const character of text) {
      if (character === term[at]) {
        at++;
      }

      if (at === term.length) {
        return true;
      }
    }

    return false;
  });
}

/** Every node at or under `rootId`, in taxonomy order. */
export function subtree(nodes: readonly TaxonomyNode[], rootId: string): TaxonomyNode[] {
  const included = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const node of nodes) {
      if (
        !included.has(node.concept_id) &&
        node.parent_concept_id &&
        included.has(node.parent_concept_id)
      ) {
        included.add(node.concept_id);
        grew = true;
      }
    }
  }

  return nodes.filter((node) => included.has(node.concept_id));
}
